import type { AdSizeSlotPayload, AdSizesTrackData } from "common-types";
import { send } from "./sender";
import type { SlotUid } from "../utils/slotUid";
import type { AdSizeInfoSenderBody } from "../types";
import {
  getArticleId,
  getBeaconId,
  getPageLang,
  getSessionId,
  getTrackingPv,
  getTrackingPvGlobal,
  getTrackingPvUid,
  getUserId,
  getWikiId,
  getWikiName,
  getWikiSkin,
} from "../utils/beaconParams";
import { getExpBucket } from "utils";

/**
 * This value is used when we expect a string in the data, but we have reached
 * a part of the code that we expect to be unreachable. Rather than using a generic
 * "unknown" string (which could be an expected value in some cases) this will specifically
 * show up in the data as an error type indicating something that needs investigating.
 */
const ERROR_DATA_STRING = "error";

export async function sendAdSizeInfo(
  { payload }: AdSizesTrackData,
  slotUid: SlotUid,
): Promise<void> {
  const geo = window.fandomHost!.geo!;
  const slotName = getSlotName(payload);

  const body = {
    slot_size: getCreativeSize(payload),
    real_ad_size: getRealAdSize(slotName),
    requested_sizes: getRequestedSizes(payload),

    slot_id: slotUid.getUid(getSlotName(payload)),
    position: getSlotName(payload),
    country_code: geo.country,
    region: geo.region,
    rv: getRv(payload),
    ...getResponseInfo(payload),
    session_id: getSessionId(),
    pv_number: getTrackingPv(),
    pv_number_global: getTrackingPvGlobal(),
    pv_unique_id: getTrackingPvUid(),
    beacon: getBeaconId(),
    c: getWikiId(),
    x: getWikiName(),
    lc: getPageLang(),
    s: getWikiSkin(),
    ua: window.navigator.userAgent,
    u: getUserId(),
    a: getArticleId(),
    n: -1,
    exp_bucket: getExpBucket(),
  } as AdSizeInfoSenderBody;

  send("adengadsizeinfo", body);
}

export interface AdSize {
  width: number;
  height: number;
}

function getResponseInfo(
  payload: AdSizesTrackData["payload"],
): Partial<AdSizeInfoSenderBody> {
  const slotInfo = payload.slotInfo ?? {};

  return {
    advertiser_id: `${slotInfo.advertiserId ?? ""}`,
    creative_id: `${slotInfo.creativeId ?? ""}`,
    line_item_id: `${slotInfo.lineItemId ?? ""}`,
    order_id: `${slotInfo.orderId ?? ""}`,
  };
}

function getAdSize(slotName: string): AdSize | undefined {
  return getIframeAdSize(slotName) ?? getDivAdSize(slotName);
}

function getIframeAdSize(slotName: string): AdSize | undefined {
  const elemIframe = getIframeAdElement(slotName);

  if (!elemIframe) {
    return undefined;
  }

  const { offsetHeight: height, offsetWidth: width } = elemIframe;

  return height && width ? { height, width } : undefined;
}

function getIframeAdElement(
  slotName: string,
): HTMLHtmlElement | null | undefined {
  return (
    document.querySelector(
      `#${slotName} [id*='google_ads_iframe_\\/5441'] iframe`,
    ) as HTMLIFrameElement
  )?.contentWindow?.document?.querySelector("html");
}

function getDivAdSize(slotName: string): AdSize | undefined {
  const element: HTMLElement = document.querySelector(
    `#${slotName} [id*='google_ads_iframe_\\/5441'] div`,
  )?.firstElementChild as HTMLElement;

  if (!element) {
    return undefined;
  }

  const { clientHeight: height, clientWidth: width } = element;

  return height && width ? { height, width } : undefined;
}

function getSizeFromElement(slotName: string): AdSize {
  const element = document.getElementById(slotName);
  return {
    width: element?.offsetWidth ?? 0,
    height: element?.offsetHeight ?? 0,
  };
}

function getIframeSize(slotName: string): AdSize | undefined {
  const element = document.querySelector(`#${slotName} iframe`);

  if (!element) {
    return undefined;
  }

  const { clientHeight: height, clientWidth: width } = element;

  return height && width ? { height, width } : undefined;
}

/**
 * Measures the actual size within the rendered creative. Under normal operation,
 * this should be the same as the getCreativeSize identified by the render event.
 * A difference between these values indicates a bad ad.
 * This function utilizes the CSS.escape function for slot names that have
 * special characters such as '/'.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/CSS/escape}
 */
function getRealAdSize(slotName: string): string {
  // This is a safety check for outOfPage slots or slots that have ids that need special character escaping.
  // An example of this is the 'interstitial' slot, which is an outOfPage slot, with an id in the form of gpt_unit/5441/wka1b.IU/interstitial...etc/
  // Since the below functions use document.querySelector to select the slotname, we need to escape the '/', since it's not a valid css selector syntax. To accompolish this, we can use the CSS.escape method to escape the slot name properly.
  const escapedSlotName = CSS.escape(slotName);
  // Fallback to container size when ad is rendered outside the iframe
  // or element size if container and iframe do not provide a proper size
  const { width, height } =
    getIframeSize(escapedSlotName) ??
    getAdSize(escapedSlotName) ??
    getSizeFromElement(escapedSlotName);

  if (!width || !height) {
    return "unknown";
  }

  return `${width}x${height}`;
}

/**
 * Returns the size that was chosen to be rendered, identified by the render event
 */
function getCreativeSize(payload: AdSizeSlotPayload): string {
  if (payload.slotInfo?.selectedSize === null) {
    // TODO check with stakeholders if this more precise return value is preferred
    //return "empty_slot";
    return "unknown";
  } else if (payload.slotInfo?.selectedSize) {
    return payload.slotInfo.selectedSize;
  }
  //This indicates bad input - we should not receive undefined or "" here.
  return ERROR_DATA_STRING;
}

/**
 * These are the possible sizes that were sent in the GAM request
 */
function getRequestedSizes(payload: AdSizeSlotPayload): string {
  try {
    const sizesString = payload.slotInfo.parsedSizes.join(",");

    // This occurs if we were unable to parse the sizes from the contentUrl
    // and we receive an empty array
    if (sizesString === "") {
      return "unknown";
    }
    return sizesString;
  } catch {
    // occurs when parsedSizes is missing or is not an array as expected
    return ERROR_DATA_STRING;
  }
}

function getSlotName(payload: AdSizesTrackData["payload"]): string {
  return payload.slotName ?? "unknown";
}

function getRv(payload: AdSizesTrackData["payload"]): number {
  return payload.rv ?? -1;
}
